import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Logo } from '@/components/shared/Logo'
import { portalApi } from '@/api/portal'

const apiBase = import.meta.env.VITE_API_URL ?? ''

type Tab = 'password' | 'link'

export default function LoginPage() {
  const { login, user } = useAuth()
  const [tab, setTab] = useState<Tab>('password')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [linkEmail, setLinkEmail] = useState('')
  const [linkSent, setLinkSent] = useState(false)
  const [linkLoading, setLinkLoading] = useState(false)

  if (user) {
    if (user.role === 'SuperAdmin') return <Navigate to="/superadmin/tenants" replace />
    if (user.role === 'TenantAdmin') return <Navigate to="/admin/overview" replace />
    return <Navigate to="/portal/overview" replace />
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setError(
        status === 429
          ? 'Too many failed attempts. Account locked for 15 minutes.'
          : 'Invalid email or password.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLinkLoading(true)
    try {
      await portalApi.requestMagicLink(linkEmail.trim())
    } catch {
      // fail silently — don't reveal whether the address exists
    } finally {
      setLinkLoading(false)
      setLinkSent(true)
    }
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between w-2/5 bg-gradient-to-br from-indigo-600 to-indigo-800 p-12">
        <Logo size={64} theme="dark" />
        <div>
          <p className="text-indigo-200 text-sm font-medium uppercase tracking-widest mb-3">Training compliance</p>
          <p className="text-white text-3xl font-semibold leading-snug">
            Training management software for training companies.
          </p>
        </div>
        <div>
          <p className="text-indigo-300 text-sm">© {new Date().getFullYear()} CertSlate</p>
          <p className="text-indigo-400 text-xs mt-1 leading-relaxed">CertSlate is a trading name of DJRS Care Services Ltd. (company number: 17074229)</p>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center px-8">
        <div className="w-full max-w-sm">
          <div className="mb-6 lg:hidden"><Logo size={44} theme="light" /></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Welcome back</h2>
          <p className="text-slate-500 text-sm mb-6">Sign in to your account to continue</p>

          {/* Tabs */}
          <div className="flex rounded-lg border border-gray-200 p-1 mb-6 bg-gray-50">
            {(['password', 'link'] as Tab[]).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => { setTab(t); setError(''); setLinkSent(false) }}
                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                  tab === t
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t === 'password' ? 'Password' : 'Email link'}
              </button>
            ))}
          </div>

          {tab === 'password' ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-indigo-600 hover:text-indigo-700">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          ) : linkSent ? (
            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-5 text-center space-y-2">
              <p className="text-sm font-medium text-green-800">Check your email</p>
              <p className="text-sm text-green-700">
                If <strong>{linkEmail}</strong> is registered as a client portal user, you'll receive a sign-in link shortly.
              </p>
              <button
                type="button"
                onClick={() => { setLinkSent(false); setLinkEmail('') }}
                className="text-xs text-green-600 underline hover:text-green-700 cursor-pointer"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleLinkSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="link-email">Email</Label>
                <Input
                  id="link-email"
                  type="email"
                  autoComplete="email"
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <p className="text-xs text-slate-500">
                We'll email you a one-time sign-in link. No password needed.
              </p>
              <Button type="submit" className="w-full" disabled={linkLoading}>
                {linkLoading ? 'Sending…' : 'Send sign-in link'}
              </Button>
            </form>
          )}

          {/* Legal footer */}
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-center gap-4 text-xs text-gray-400">
            <a href={`${apiBase}/api/v1/legal/privacy-policy`} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
              Privacy Policy
            </a>
            <span>·</span>
            <a href={`${apiBase}/api/v1/legal/terms`} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
              Terms &amp; Conditions
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
